import { useState, useEffect, useRef, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './AdminDashboard.css';
import AdminAnalyticsTab from '../components/AdminAnalyticsTab'

/* ══════════════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════════════ */
const TABS = [
  { key: 'overview',    icon: '⚡', label: 'Overview'    },
  { key: 'decorators',  icon: '🎨', label: 'Decorators'  },
  { key: 'bookings',    icon: '📋', label: 'Bookings'    },
  { key: 'users',       icon: '👥', label: 'Users'       },
  { key: 'revenue',     icon: '💰', label: 'Revenue'     },
  { key: 'broadcast',   icon: '📢', label: 'Broadcast'   },
  { key: 'settings',    icon: '⚙️', label: 'Settings'    },
  { key: 'analytics', icon: '📊', label: 'Analytics' },
];

const STATUS_COLOR = {
  pending:     { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  confirmed:   { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  in_progress: { bg: '#ede9fe', text: '#5b21b6', dot: '#8b5cf6' },
  completed:   { bg: '#dcfce7', text: '#14532d', dot: '#10b981' },
  cancelled:   { bg: '#fee2e2', text: '#7f1d1d', dot: '#ef4444' },
  disputed:    { bg: '#ffe4e6', text: '#881337', dot: '#dc2626' },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/* ══════════════════════════════════════════════════════════
   MINI COMPONENTS
══════════════════════════════════════════════════════════ */

// Animated number counter
function Counter({ value, prefix = '', suffix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    const start  = prevRef.current;
    const end    = parseFloat(value) || 0;
    const dur    = 800;
    const t0     = performance.now();
    const step   = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (end - start) * ease);
      if (p < 1) requestAnimationFrame(step);
      else prevRef.current = end;
    };
    requestAnimationFrame(step);
  }, [value]);
  return <>{prefix}{decimals ? display.toFixed(decimals) : Math.floor(display)}{suffix}</>;
}

// Sparkline bar chart
function Sparkline({ data = [], color = '#C8860A', height = 40 }) {
  if (!data.length) return null;
  const max  = Math.max(...data, 1);
  const w    = 100 / data.length;
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      {data.map((v, i) => {
        const barH = (v / max) * height;
        return (
          <rect
            key={i}
            x={i * w + 0.5} y={height - barH}
            width={w - 1} height={barH}
            rx="1.5"
            fill={color}
            opacity={0.15 + (v / max) * 0.85}
          />
        );
      })}
    </svg>
  );
}

// Status badge
function Badge({ status, label }) {
  const c = STATUS_COLOR[status] || { bg: '#f1f5f9', text: '#475569', dot: '#94a3b8' };
  return (
    <span className="adm-badge" style={{ background: c.bg, color: c.text }}>
      <span style={{ background: c.dot }} className="adm-badge-dot" />
      {label || status?.replace('_', ' ')}
    </span>
  );
}

// Stat card
function StatCard({ icon, label, value, sub, color, trend, sparkData, prefix = '', suffix = '', delay = 0 }) {
  return (
    <div className="adm-stat-card" style={{ '--c': color, animationDelay: `${delay}s` }}>
      <div className="adm-stat-top">
        <div className="adm-stat-icon-wrap" style={{ background: color + '18' }}>
          <span>{icon}</span>
        </div>
        {trend !== undefined && (
          <span className={`adm-trend ${trend >= 0 ? 'up' : 'down'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="adm-stat-value" style={{ color }}>
        <Counter value={value} prefix={prefix} suffix={suffix} decimals={value % 1 !== 0 ? 1 : 0} />
      </div>
      <div className="adm-stat-label">{label}</div>
      {sub && <div className="adm-stat-sub">{sub}</div>}
      {sparkData && <div className="adm-stat-spark"><Sparkline data={sparkData} color={color} /></div>}
    </div>
  );
}

// Search input
function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="adm-search-wrap">
      <span className="adm-search-icon">🔍</span>
      <input
        className="adm-search"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && <button className="adm-search-clear" onClick={() => onChange('')}>✕</button>}
    </div>
  );
}

// Confirm modal
function ConfirmModal({ open, title, message, onConfirm, onCancel, danger }) {
  if (!open) return null;
  return (
    <div className="adm-modal-overlay" onClick={onCancel}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-icon">{danger ? '⚠️' : '❓'}</div>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="adm-modal-actions">
          <button className="adm-btn adm-btn-ghost" onClick={onCancel}>Nahi</button>
          <button className={`adm-btn ${danger ? 'adm-btn-danger' : 'adm-btn-primary'}`} onClick={onConfirm}>
            Haan, Karo
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const [stats, setStats]                     = useState(null);
  const [decorators, setDecorators]           = useState([]);
  const [bookings, setBookings]               = useState([]);
  const [users, setUsers]                     = useState([]);
  const [commissionSummary, setCommissionSummary] = useState([]);
  const [activeTab, setActiveTab]             = useState('overview');
  const [loading, setLoading]                 = useState(true);
  const [sidebarOpen, setSidebarOpen]         = useState(true);
  const [actionLoading, setActionLoading]     = useState({});

  // Search & filter states
  const [searchUser, setSearchUser]           = useState('');
  const [searchDecorator, setSearchDecorator] = useState('');
  const [bookingFilter, setBookingFilter]     = useState('');
  const [decoratorFilter, setDecoratorFilter] = useState('');

  // Broadcast form
  const [broadcastForm, setBroadcastForm] = useState({
    title: '', message: '', roles: ['client', 'decorator'], targetCity: ''
  });
  const [broadcasting, setBroadcasting] = useState(false);

  // Confirm modal
  const [confirm, setConfirm] = useState({ open: false });

  // Revenue chart period
  const [revPeriod, setRevPeriod] = useState('month');

  const setAction = (key, val) => setActionLoading(p => ({ ...p, [key]: val }));

  /* ── LOAD DATA ─────────────────────────────────────── */
  useEffect(() => {
    Promise.all([
      adminAPI.getStats(),
      adminAPI.getDecorators(),
      adminAPI.getBookings(),
      adminAPI.getUsers(),
    ]).then(([s, d, b, u]) => {
      setStats(s.data);
      setDecorators(d.data?.decorators || d.data || []);
      setBookings(b.data?.bookings || b.data || []);
      setUsers(u.data?.users || u.data || []);
    }).catch(() => toast.error('Data load nahi hua'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab === 'revenue') {
      adminAPI.getCommissionSummary?.()
        .then(r => setCommissionSummary(r.data?.summary || []))
        .catch(() => {});
    }
  }, [activeTab]);

  /* ── RELOAD ────────────────────────────────────────── */
  const reload = useCallback(async () => {
    const [d, u, s] = await Promise.all([
      adminAPI.getDecorators(),
      adminAPI.getUsers(),
      adminAPI.getStats(),
    ]);
    setDecorators(d.data?.decorators || d.data || []);
    setUsers(u.data?.users || u.data || []);
    setStats(s.data);
  }, []);

  /* ── ACTIONS ───────────────────────────────────────── */
  const handleVerify = async (id, name) => {
    setAction(id + '_verify', true);
    try {
      await adminAPI.verifyDecorator(id);
      toast.success(`✅ ${name} verify ho gaya!`);
      reload();
    } catch { toast.error('Verify nahi hua'); }
    finally { setAction(id + '_verify', false); }
  };

  const handleUnverify = async (id, name) => {
    const reason = window.prompt('Unverify reason likho:');
    if (!reason) return;
    setAction(id + '_unverify', true);
    try {
      await adminAPI.unverifyDecorator(id, reason);
      toast.success(`${name} unverify ho gaya`);
      reload();
    } catch { toast.error('Unverify nahi hua'); }
    finally { setAction(id + '_unverify', false); }
  };

  const handleFeature = async (id, name, isFeatured) => {
    setAction(id + '_feature', true);
    try {
      await adminAPI.featureDecorator(id, 1, !isFeatured);
      toast.success(`Featured ${!isFeatured ? 'set' : 'remove'} ho gaya!`);
      reload();
    } catch { toast.error('Feature fail'); }
    finally { setAction(id + '_feature', false); }
  };

  const handleToggleUser = (id, name, isActive) => {
    setConfirm({
      open: true,
      title: isActive ? `${name} ko Block Karo?` : `${name} ko Unblock Karo?`,
      message: isActive ? 'Ye user platform use nahi kar payega.' : 'User phir se access le payega.',
      danger: isActive,
      onConfirm: async () => {
        setConfirm({ open: false });
        setAction(id + '_toggle', true);
        try {
          await adminAPI.toggleUser(id);
          toast.success(`${name} ${isActive ? 'block' : 'unblock'} ho gaya!`);
          reload();
        } catch { toast.error('Action fail hua'); }
        finally { setAction(id + '_toggle', false); }
      },
    });
  };

  const handleDeleteUser = (id, name) => {
    setConfirm({
      open: true,
      title: `${name} ko Delete Karo?`,
      message: 'Ye action undo nahi ho sakta. Permanently delete ho jaayega.',
      danger: true,
      onConfirm: async () => {
        setConfirm({ open: false });
        setAction(id + '_delete', true);
        try {
          await adminAPI.deleteUser(id);
          toast.success(`${name} delete ho gaya`);
          reload();
        } catch { toast.error('Delete fail'); }
        finally { setAction(id + '_delete', false); }
      },
    });
  };

  const handleResolveDispute = async (id, action) => {
    const resolution = window.prompt('Resolution likho:');
    if (!resolution) return;
    try {
      await adminAPI.resolveDispute(id, { resolution, action });
      toast.success('Dispute resolve ho gaya');
      const b = await adminAPI.getBookings();
      setBookings(b.data?.bookings || b.data || []);
    } catch { toast.error('Resolve fail'); }
  };

  const handleCollectCommission = async (decoratorId, name, pending) => {
    const amount = window.prompt(`${name} se commission collect karo\nPending: ₹${pending}\nAmount:`);
    if (!amount || isNaN(amount)) return;
    setAction(decoratorId + '_commission', true);
    try {
      await adminAPI.collectCommission(decoratorId, Number(amount));
      toast.success(`₹${amount} collect ho gaya!`);
      const [r, s] = await Promise.all([adminAPI.getCommissionSummary?.(), adminAPI.getStats()]);
      if (r) setCommissionSummary(r.data?.summary || []);
      setStats(s.data);
    } catch { toast.error('Commission collect fail'); }
    finally { setAction(decoratorId + '_commission', false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.message)
      return toast.error('Title aur message dono chahiye');
    setBroadcasting(true);
    try {
      const { data } = await adminAPI.broadcast(broadcastForm);
      toast.success(data.message);
      setBroadcastForm({ title: '', message: '', roles: ['client', 'decorator'], targetCity: '' });
    } catch { toast.error('Broadcast fail'); }
    finally { setBroadcasting(false); }
  };

  /* ── DERIVED ────────────────────────────────────────── */
  const pendingDecs  = decorators.filter(d => !d.isVerified);
  const disputedBks  = bookings.filter(b => b.status === 'disputed');
  const verifiedDecs = decorators.filter(d => d.isVerified);

  const filteredUsers = users.filter(u =>
    !searchUser ||
    u.name?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.phone?.includes(searchUser) ||
    u.email?.toLowerCase().includes(searchUser.toLowerCase())
  );

  const filteredDecs = decorators.filter(d => {
    const matchSearch = !searchDecorator ||
      d.businessName?.toLowerCase().includes(searchDecorator.toLowerCase()) ||
      d.user?.name?.toLowerCase().includes(searchDecorator.toLowerCase()) ||
      d.user?.phone?.includes(searchDecorator);
    const matchFilter =
      !decoratorFilter ||
      (decoratorFilter === 'verified' && d.isVerified) ||
      (decoratorFilter === 'pending' && !d.isVerified) ||
      (decoratorFilter === 'featured' && d.isFeatured);
    return matchSearch && matchFilter;
  });

  const filteredBks = bookings.filter(b =>
    !bookingFilter || b.status === bookingFilter
  );

  // Fake sparkline data from bookings
  const bookingsByMonth = Array(6).fill(0).map((_, i) => {
    const m = (new Date().getMonth() - 5 + i + 12) % 12;
    return bookings.filter(b => new Date(b.createdAt).getMonth() === m).length;
  });

  const revenueByMonth = Array(6).fill(0).map((_, i) => {
    const m = (new Date().getMonth() - 5 + i + 12) % 12;
    return bookings
      .filter(b => new Date(b.createdAt).getMonth() === m && b.status === 'completed')
      .reduce((sum, b) => sum + (b.commissionAmount || 0), 0);
  });

  /* ── LOADING ────────────────────────────────────────── */
  if (loading) return (
    <div className="adm-loading-screen">
      <div className="adm-loading-inner">
        <div className="adm-loading-logo">🛡️</div>
        <div className="adm-loading-bar"><div className="adm-loading-fill" /></div>
        <p>Admin Panel load ho raha hai...</p>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <div className={`adm-root ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>

      {/* ── SIDEBAR ─────────────────────────────────── */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar-header">
          <div className="adm-sidebar-logo">
            <span>🛡️</span>
            <div className={`adm-sidebar-title ${!sidebarOpen ? 'hidden' : ''}`}>
              <strong>DecorHub</strong>
              <span>Admin Panel</span>
            </div>
          </div>
          <button className="adm-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <nav className="adm-nav">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`adm-nav-item ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="adm-nav-icon">{tab.icon}</span>
              <span className={`adm-nav-label ${!sidebarOpen ? 'hidden' : ''}`}>{tab.label}</span>
              {tab.key === 'overview' && pendingDecs.length > 0 && (
                <span className="adm-nav-badge">{pendingDecs.length}</span>
              )}
              {tab.key === 'bookings' && disputedBks.length > 0 && (
                <span className="adm-nav-badge danger">{disputedBks.length}</span>
              )}
            </button>
          ))}
        </nav>

        <div className={`adm-sidebar-footer ${!sidebarOpen ? 'hidden' : ''}`}>
          <div className="adm-online-dot" />
          <span>System Online</span>
        </div>
      </aside>

      {/* ── MAIN CONTENT ────────────────────────────── */}
      <main className="adm-main">

        {/* Top bar */}
        <div className="adm-topbar">
          <div className="adm-topbar-left">
            <h2 className="adm-page-title">
              {TABS.find(t => t.key === activeTab)?.icon}{' '}
              {TABS.find(t => t.key === activeTab)?.label}
            </h2>
            <span className="adm-page-date">
              {new Date().toLocaleDateString('hi-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>
          <div className="adm-topbar-right">
            {(pendingDecs.length > 0 || disputedBks.length > 0) && (
              <div className="adm-alert-pills">
                {pendingDecs.length > 0 && (
                  <button className="adm-alert-pill warning" onClick={() => setActiveTab('overview')}>
                    ⏳ {pendingDecs.length} Pending
                  </button>
                )}
                {disputedBks.length > 0 && (
                  <button className="adm-alert-pill danger" onClick={() => setActiveTab('bookings')}>
                    🚨 {disputedBks.length} Disputes
                  </button>
                )}
              </div>
            )}
            <div className="adm-topbar-avatar">A</div>
          </div>
        </div>

        <div className="adm-content">

          {/* ══════════════════════════════════════════
              OVERVIEW TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <div className="adm-tab-content">

              {/* KPI Cards */}
              <div className="adm-stats-grid">
                <StatCard icon="👥" label="Total Users"     value={stats?.totalUsers || 0}       color="#3b82f6" sub="100% FREE platform"   trend={12} sparkData={bookingsByMonth} delay={0}   />
                <StatCard icon="🎨" label="Decorators"      value={stats?.totalDecorators || 0}  color="#C8860A" sub="3% commission model"   trend={3}  sparkData={bookingsByMonth} delay={0.05}/>
                <StatCard icon="📋" label="Total Bookings"  value={stats?.totalBookings || 0}    color="#8b5cf6" trend={15} sparkData={bookingsByMonth} delay={0.1} />
                <StatCard icon="✅" label="Completed"       value={stats?.completedBookings || 0} color="#10b981" trend={5}  sparkData={bookingsByMonth} delay={0.15}/>
                <StatCard icon="💰" label="Platform Revenue" value={(stats?.totalPlatformRevenue || 0) / 1000} color="#10b981" prefix="₹" suffix="k" trend={20} delay={0.2} />
                <StatCard icon="⏳" label="Pending Verify"  value={stats?.pendingVerification || 0} color="#f59e0b" sub="Action needed" delay={0.25}/>
              </div>

              {/* Revenue + Bookings mini charts */}
              <div className="adm-charts-row">
                <div className="adm-chart-card">
                  <div className="adm-chart-header">
                    <h4>📈 Bookings — Last 6 Months</h4>
                    <span className="adm-chart-total">{stats?.totalBookings || 0} total</span>
                  </div>
                  <div className="adm-bar-chart">
                    {bookingsByMonth.map((v, i) => {
                      const max = Math.max(...bookingsByMonth, 1);
                      const m   = (new Date().getMonth() - 5 + i + 12) % 12;
                      return (
                        <div key={i} className="adm-bar-col">
                          <div className="adm-bar-fill" style={{ height: `${(v / max) * 100}%`, background: '#3b82f6' }}>
                            <span className="adm-bar-tooltip">{v}</span>
                          </div>
                          <span className="adm-bar-label">{MONTHS[m]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="adm-chart-card">
                  <div className="adm-chart-header">
                    <h4>💰 Revenue — Last 6 Months</h4>
                    <span className="adm-chart-total">₹{(stats?.totalPlatformRevenue || 0).toLocaleString()}</span>
                  </div>
                  <div className="adm-bar-chart">
                    {revenueByMonth.map((v, i) => {
                      const max = Math.max(...revenueByMonth, 1);
                      const m   = (new Date().getMonth() - 5 + i + 12) % 12;
                      return (
                        <div key={i} className="adm-bar-col">
                          <div className="adm-bar-fill" style={{ height: `${(v / max) * 100}%`, background: '#C8860A' }}>
                            <span className="adm-bar-tooltip">₹{(v / 1000).toFixed(1)}k</span>
                          </div>
                          <span className="adm-bar-label">{MONTHS[m]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Booking status breakdown */}
              <div className="adm-status-breakdown">
                <h4>📊 Booking Status Breakdown</h4>
                <div className="adm-status-grid">
                  {Object.entries(STATUS_COLOR).map(([status, colors]) => {
                    const count = bookings.filter(b => b.status === status).length;
                    const pct   = bookings.length ? Math.round(count / bookings.length * 100) : 0;
                    return (
                      <div key={status} className="adm-status-item">
                        <div className="adm-status-dot" style={{ background: colors.dot }} />
                        <div className="adm-status-info">
                          <span className="adm-status-name">{status.replace('_', ' ')}</span>
                          <div className="adm-status-bar-wrap">
                            <div className="adm-status-bar" style={{ width: `${pct}%`, background: colors.dot }} />
                          </div>
                        </div>
                        <span className="adm-status-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pending verifications */}
              {pendingDecs.length > 0 && (
                <div className="adm-section">
                  <div className="adm-section-header">
                    <h4>⏳ Verification Queue ({pendingDecs.length})</h4>
                  </div>
                  <div className="adm-list">
                    {pendingDecs.map(d => (
                      <div key={d._id} className="adm-decorator-card pending">
                        <div className="adm-decorator-main">
                          <div className="adm-decorator-avatar" style={{ background: '#fef3c7', color: '#92400e' }}>
                            {d.businessName?.[0] || '?'}
                          </div>
                          <div className="adm-decorator-info">
                            <div className="adm-decorator-name-row">
                              <h3>{d.businessName}</h3>
                              <Badge status="pending" label="⏳ Pending" />
                            </div>
                            <p>👤 {d.user?.name} &nbsp;·&nbsp; 📞 {d.user?.phone} &nbsp;·&nbsp; 📍 {d.city}</p>
                            <p>🎨 {d.services?.slice(0, 3).join(', ')} &nbsp;·&nbsp; 💰 ₹{(d.priceRange?.min / 1000).toFixed(0)}k–₹{(d.priceRange?.max / 1000).toFixed(0)}k &nbsp;·&nbsp; 📸 {d.portfolio?.length || 0} photos</p>
                          </div>
                        </div>
                        {d.portfolio?.length > 0 && (
                          <div className="adm-portfolio-strip">
                            {d.portfolio.slice(0, 5).map((p, i) => (
                              <img key={i} src={p.image} alt="" className="adm-portfolio-thumb" />
                            ))}
                          </div>
                        )}
                        <div className="adm-decorator-actions">
                          <button className="adm-btn adm-btn-primary" onClick={() => handleVerify(d._id, d.businessName)} disabled={actionLoading[d._id + '_verify']}>
                            {actionLoading[d._id + '_verify'] ? <span className="adm-spinner" /> : '✅ Verify Karo'}
                          </button>
                          <button className="adm-btn adm-btn-ghost" onClick={() => handleFeature(d._id, d.businessName, d.isFeatured)} disabled={actionLoading[d._id + '_feature']}>
                            {d.isFeatured ? '★ Unfeature' : '⭐ Feature'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pendingDecs.length === 0 && (
                <div className="adm-empty-state success">
                  <span>✅</span>
                  <h3>Sab Verified!</h3>
                  <p>Koi pending verification nahi hai abhi</p>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════
              DECORATORS TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'decorators' && (
            <div className="adm-tab-content">
              {/* Stats row */}
              <div className="adm-mini-stats">
                {[
                  { label: 'Total',    value: decorators.length,    color: '#3b82f6' },
                  { label: 'Verified', value: verifiedDecs.length,  color: '#10b981' },
                  { label: 'Pending',  value: pendingDecs.length,   color: '#f59e0b' },
                  { label: 'Featured', value: decorators.filter(d => d.isFeatured).length, color: '#C8860A' },
                ].map(s => (
                  <div key={s.label} className="adm-mini-stat" style={{ '--c': s.color }}>
                    <span className="adm-mini-val" style={{ color: s.color }}>{s.value}</span>
                    <span className="adm-mini-lbl">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div className="adm-controls-row">
                <SearchInput value={searchDecorator} onChange={setSearchDecorator} placeholder="Search decorator, naam, phone..." />
                <div className="adm-filter-chips">
                  {[
                    { key: '',         label: 'All' },
                    { key: 'verified', label: '✅ Verified' },
                    { key: 'pending',  label: '⏳ Pending' },
                    { key: 'featured', label: '⭐ Featured' },
                  ].map(f => (
                    <button
                      key={f.key}
                      className={`adm-filter-chip ${decoratorFilter === f.key ? 'active' : ''}`}
                      onClick={() => setDecoratorFilter(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="adm-list">
                {filteredDecs.map(d => (
                  <div key={d._id} className="adm-decorator-card">
                    <div className="adm-decorator-main">
                      <div className="adm-decorator-avatar" style={{ background: d.isVerified ? '#ecfdf5' : '#fef3c7', color: d.isVerified ? '#059669' : '#92400e' }}>
                        {d.businessName?.[0] || '?'}
                      </div>
                      <div className="adm-decorator-info">
                        <div className="adm-decorator-name-row">
                          <h3>{d.businessName}</h3>
                          <Badge status={d.isVerified ? 'completed' : 'pending'} label={d.isVerified ? '✓ Verified' : '⏳ Pending'} />
                          {d.isFeatured && <Badge status="confirmed" label="⭐ Featured" />}
                        </div>
                        <p>👤 {d.user?.name} &nbsp;·&nbsp; 📞 {d.user?.phone} &nbsp;·&nbsp; 📍 {d.city}</p>
                        <p>⭐ {d.rating || 0} rating &nbsp;·&nbsp; 📋 {d.totalBookings || 0} bookings &nbsp;·&nbsp; 💰 ₹{((d.totalEarnings || 0) / 1000).toFixed(1)}k earned</p>
                        {(d.pendingCommission || 0) > 0 && (
                          <p className="adm-warning-text">⚠️ Commission pending: ₹{d.pendingCommission?.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                    <div className="adm-decorator-actions">
                      {d.isVerified ? (
                        <button className="adm-btn adm-btn-danger-ghost" onClick={() => handleUnverify(d._id, d.businessName)} disabled={actionLoading[d._id + '_unverify']}>
                          {actionLoading[d._id + '_unverify'] ? <span className="adm-spinner" /> : '❌ Unverify'}
                        </button>
                      ) : (
                        <button className="adm-btn adm-btn-primary" onClick={() => handleVerify(d._id, d.businessName)} disabled={actionLoading[d._id + '_verify']}>
                          {actionLoading[d._id + '_verify'] ? <span className="adm-spinner" /> : '✅ Verify'}
                        </button>
                      )}
                      <button className="adm-btn adm-btn-ghost" onClick={() => handleFeature(d._id, d.businessName, d.isFeatured)} disabled={actionLoading[d._id + '_feature']}>
                        {d.isFeatured ? '★ Unfeature' : '⭐ Feature'}
                      </button>
                    </div>
                  </div>
                ))}
                {filteredDecs.length === 0 && (
                  <div className="adm-empty-state"><span>🔍</span><h3>Koi decorator nahi mila</h3></div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              BOOKINGS TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'bookings' && (
            <div className="adm-tab-content">
              {/* Status filter pills */}
              <div className="adm-status-pills">
                {['', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled', 'disputed'].map(s => {
                  const count = s ? bookings.filter(b => b.status === s).length : bookings.length;
                  return (
                    <button
                      key={s}
                      className={`adm-status-pill ${bookingFilter === s ? 'active' : ''} ${s === 'disputed' && count > 0 ? 'pulse' : ''}`}
                      onClick={() => setBookingFilter(s)}
                      style={bookingFilter === s && s ? { background: STATUS_COLOR[s]?.dot, color: 'white', borderColor: STATUS_COLOR[s]?.dot } : {}}
                    >
                      {s || 'Sab'} <span className="adm-pill-count">{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="adm-list">
                {filteredBks.map(b => (
                  <div key={b._id} className="adm-booking-card" style={{ '--status-color': STATUS_COLOR[b.status]?.dot || '#e2e8f0' }}>
                    <div className="adm-booking-left">
                      <div className="adm-booking-status-bar" />
                      <div className="adm-booking-info">
                        <div className="adm-booking-title-row">
                          <h3>{b.eventType}</h3>
                          <Badge status={b.status} />
                          {b.bookingNumber && <span className="adm-booking-num">#{b.bookingNumber}</span>}
                        </div>
                        <p>👤 {b.client?.name} &nbsp;·&nbsp; 📞 {b.client?.phone}</p>
                        <p>🎨 {b.decorator?.businessName} &nbsp;·&nbsp; 📅 {new Date(b.eventDate).toLocaleDateString('hi-IN')} &nbsp;·&nbsp; 📍 {b.venue?.city}</p>
                        {b.dispute?.reason && (
                          <p className="adm-warning-text">🚨 {b.dispute.reason}</p>
                        )}
                      </div>
                    </div>
                    <div className="adm-booking-right">
                      <div className="adm-booking-amount">₹{b.totalAmount?.toLocaleString()}</div>
                      <div className="adm-booking-commission">Commission: ₹{b.commissionAmount?.toLocaleString()}</div>
                      <div className="adm-booking-dec-amt">Decorator: ₹{b.decoratorAmount?.toLocaleString()}</div>
                      {b.status === 'disputed' && (
                        <div className="adm-dispute-actions">
                          <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => handleResolveDispute(b._id, 'no_action')}>✅ Resolve</button>
                          <button className="adm-btn adm-btn-danger-ghost adm-btn-sm" onClick={() => handleResolveDispute(b._id, 'refund')}>💸 Refund</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredBks.length === 0 && (
                  <div className="adm-empty-state"><span>📋</span><h3>Koi booking nahi</h3></div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              USERS TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'users' && (
            <div className="adm-tab-content">
              <div className="adm-free-banner">
                🎉 <strong>Users ke liye platform BILKUL FREE — Zero charges hamesha</strong>
              </div>

              {/* User stats */}
              <div className="adm-mini-stats">
                {[
                  { label: 'Total',      value: users.length,                            color: '#3b82f6' },
                  { label: 'Active',     value: users.filter(u => u.isActive).length,    color: '#10b981' },
                  { label: 'Blocked',    value: users.filter(u => !u.isActive).length,   color: '#ef4444' },
                  { label: 'Decorators', value: users.filter(u => u.role === 'decorator').length, color: '#C8860A' },
                ].map(s => (
                  <div key={s.label} className="adm-mini-stat" style={{ '--c': s.color }}>
                    <span className="adm-mini-val" style={{ color: s.color }}>{s.value}</span>
                    <span className="adm-mini-lbl">{s.label}</span>
                  </div>
                ))}
              </div>

              <SearchInput value={searchUser} onChange={setSearchUser} placeholder="Search by naam, phone, email..." />

              <div className="adm-list" style={{ marginTop: '16px' }}>
                {filteredUsers.map(u => (
                  <div key={u._id} className="adm-user-card">
                    <div className="adm-user-avatar" style={{ background: u.role === 'decorator' ? '#fef3c7' : '#dbeafe', color: u.role === 'decorator' ? '#92400e' : '#1e40af' }}>
                      {u.name?.[0] || '?'}
                    </div>
                    <div className="adm-user-info">
                      <div className="adm-user-name-row">
                        <h3>{u.name}</h3>
                        <span className={`adm-user-status ${u.isActive ? 'active' : 'blocked'}`}>
                          {u.isActive ? '● Active' : '● Blocked'}
                        </span>
                        <span className="adm-role-tag">{u.role}</span>
                        <span className="adm-free-tag">FREE</span>
                      </div>
                      <p>📧 {u.email} &nbsp;·&nbsp; 📞 {u.phone || 'N/A'}</p>
                      <p className="adm-user-date">Joined: {new Date(u.createdAt).toLocaleDateString('hi-IN')}</p>
                    </div>
                    <div className="adm-user-actions">
                      <button
                        className={`adm-btn adm-btn-sm ${u.isActive ? 'adm-btn-danger-ghost' : 'adm-btn-success'}`}
                        onClick={() => handleToggleUser(u._id, u.name, u.isActive)}
                        disabled={actionLoading[u._id + '_toggle']}
                      >
                        {actionLoading[u._id + '_toggle'] ? <span className="adm-spinner" /> : u.isActive ? '🚫 Block' : '✅ Unblock'}
                      </button>
                      <button
                        className="adm-btn adm-btn-sm adm-btn-danger-ghost"
                        onClick={() => handleDeleteUser(u._id, u.name)}
                        disabled={actionLoading[u._id + '_delete']}
                      >
                        {actionLoading[u._id + '_delete'] ? <span className="adm-spinner" /> : '🗑️'}
                      </button>
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="adm-empty-state"><span>👥</span><h3>Koi user nahi mila</h3></div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              REVENUE TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'revenue' && (
            <div className="adm-tab-content">
              {/* Revenue KPIs */}
              <div className="adm-stats-grid">
                <StatCard icon="💰" label="Total Revenue"       value={(stats?.totalPlatformRevenue || 0) / 1000} color="#10b981" prefix="₹" suffix="k" trend={20} delay={0}    />
                <StatCard icon="⏳" label="Pending Collection"  value={(stats?.pendingCommission || 0) / 1000}   color="#f59e0b" prefix="₹" suffix="k" delay={0.05} />
                <StatCard icon="📊" label="Booking Value Total" value={(stats?.totalBookingValue || 0) / 1000}   color="#6366f1" prefix="₹" suffix="k" delay={0.1}  />
                <StatCard icon="💎" label="Decorator Earnings"  value={(stats?.totalDecoratorEarnings || 0) / 1000} color="#8b5cf6" prefix="₹" suffix="k" delay={0.15}/>
              </div>

              {/* Commission info */}
              <div className="adm-commission-banner">
                <div className="adm-commission-rate">3%</div>
                <div>
                  <h4>Fixed Commission Rate</h4>
                  <p>Har completed booking pe decorator se 3% commission collect karo. Client ke liye ZERO.</p>
                </div>
              </div>

              {/* Commission table */}
              <div className="adm-section">
                <h4>💰 Commission Collection</h4>
                <div className="adm-list">
                  {commissionSummary.length === 0 ? (
                    <div className="adm-empty-state success">
                      <span>✅</span><h3>Koi commission pending nahi</h3>
                    </div>
                  ) : commissionSummary.map(d => (
                    <div key={d._id} className="adm-commission-card">
                      <div className="adm-decorator-avatar" style={{ background: '#fffbeb', color: '#92400e' }}>
                        {d.businessName?.[0]}
                      </div>
                      <div className="adm-commission-info">
                        <h3>{d.businessName}</h3>
                        <p>👤 {d.userName} &nbsp;·&nbsp; 📞 {d.userPhone}</p>
                        <p>📋 {d.totalBookings} bookings &nbsp;·&nbsp; Earned: ₹{((d.totalEarnings || 0) / 1000).toFixed(1)}k &nbsp;·&nbsp; Paid: ₹{(d.paidCommission || 0).toLocaleString()}</p>
                      </div>
                      <div className="adm-commission-right">
                        <div className="adm-commission-label">PENDING</div>
                        <div className="adm-commission-amount" style={{ color: (d.pendingCommission || 0) > 0 ? '#f59e0b' : '#10b981' }}>
                          ₹{(d.pendingCommission || 0).toLocaleString()}
                        </div>
                        {(d.pendingCommission || 0) > 0 && (
                          <button
                            className="adm-btn adm-btn-primary adm-btn-sm"
                            onClick={() => handleCollectCommission(d._id, d.businessName, d.pendingCommission)}
                            disabled={actionLoading[d._id + '_commission']}
                          >
                            {actionLoading[d._id + '_commission'] ? <span className="adm-spinner" /> : '💰 Collect'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════
              BROADCAST TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'broadcast' && (
            <div className="adm-tab-content">
              <div className="adm-broadcast-wrap">
                <div className="adm-broadcast-card">
                  <div className="adm-broadcast-header">
                    <span className="adm-broadcast-icon">📢</span>
                    <div>
                      <h3>Notification Broadcast</h3>
                      <p>Sab users ko ek saath notification bhejo</p>
                    </div>
                  </div>

                  <div className="adm-form-group">
                    <label>Title *</label>
                    <input className="adm-input" placeholder="🎉 Special Offer!" value={broadcastForm.title}
                      onChange={e => setBroadcastForm(p => ({ ...p, title: e.target.value }))} />
                  </div>

                  <div className="adm-form-group">
                    <label>Message *</label>
                    <textarea className="adm-input adm-textarea" rows={4} placeholder="Apna message likho..."
                      value={broadcastForm.message}
                      onChange={e => setBroadcastForm(p => ({ ...p, message: e.target.value }))} />
                  </div>

                  <div className="adm-form-group">
                    <label>Kisko Bhejo?</label>
                    <div className="adm-role-checkboxes">
                      {[
                        { val: 'client',    label: '👥 Clients',    count: users.filter(u => u.role === 'client').length },
                        { val: 'decorator', label: '🎨 Decorators', count: decorators.length },
                      ].map(r => (
                        <label key={r.val} className={`adm-role-check ${broadcastForm.roles.includes(r.val) ? 'checked' : ''}`}>
                          <input type="checkbox" checked={broadcastForm.roles.includes(r.val)}
                            onChange={e => setBroadcastForm(p => ({
                              ...p, roles: e.target.checked ? [...p.roles, r.val] : p.roles.filter(x => x !== r.val)
                            }))} />
                          <span>{r.label}</span>
                          <span className="adm-role-count">{r.count}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="adm-form-group">
                    <label>City Filter <span className="adm-optional">(Optional)</span></label>
                    <input className="adm-input" placeholder="Jaise: Bhilai — sirf usi city ko"
                      value={broadcastForm.targetCity}
                      onChange={e => setBroadcastForm(p => ({ ...p, targetCity: e.target.value }))} />
                  </div>

                  <button className="adm-btn adm-btn-primary adm-btn-full" onClick={handleBroadcast} disabled={broadcasting}>
                    {broadcasting ? <><span className="adm-spinner" /> Bhej raha hai...</> : '📢 Broadcast Bhejo'}
                  </button>
                </div>

                {/* Preview */}
                {(broadcastForm.title || broadcastForm.message) && (
                  <div className="adm-broadcast-preview">
                    <h4>Preview</h4>
                    <div className="adm-notif-preview">
                      <div className="adm-notif-icon">🔔</div>
                      <div>
                        <strong>{broadcastForm.title || 'Title...'}</strong>
                        <p>{broadcastForm.message || 'Message...'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && <AdminAnalyticsTab />}

          {/* ══════════════════════════════════════════
              SETTINGS TAB
          ══════════════════════════════════════════ */}
          {activeTab === 'settings' && (
            <div className="adm-tab-content">
              <div className="adm-settings-grid">
                {[
                  { icon: '💰', title: 'Commission Rate', value: '3%', desc: 'Completed bookings pe platform commission', editable: false },
                  { icon: '🆓', title: 'Client Pricing',  value: 'FREE', desc: 'Clients ke liye hamesha free', editable: false },
                  { icon: '📍', title: 'Service Cities',  value: '6', desc: 'Raipur, Bhilai, Durg, Bilaspur, Korba, Rajnandgaon', editable: false },
                  { icon: '🎨', title: 'Total Decorators', value: decorators.length, desc: `${verifiedDecs.length} verified, ${pendingDecs.length} pending`, editable: false },
                  { icon: '📋', title: 'Event Types',     value: '9', desc: 'Shaadi, Birthday, Engagement aur 6 more', editable: false },
                  { icon: '⭐', title: 'Avg Platform Rating', value: '4.8', desc: 'User satisfaction score', editable: false },
                ].map(s => (
                  <div key={s.title} className="adm-setting-card">
                    <span className="adm-setting-icon">{s.icon}</span>
                    <div className="adm-setting-info">
                      <h4>{s.title}</h4>
                      <div className="adm-setting-value">{s.value}</div>
                      <p>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="adm-platform-info">
                <h4>🚀 Platform Info</h4>
                <div className="adm-info-grid">
                  <div><span>Stack</span><strong>MERN (MongoDB, Express, React, Node)</strong></div>
                  <div><span>Frontend</span><strong>Vercel</strong></div>
                  <div><span>Backend</span><strong>Railway</strong></div>
                  <div><span>Payment</span><strong>Razorpay</strong></div>
                  <div><span>Real-time</span><strong>Socket.IO</strong></div>
                  <div><span>AI</span><strong>Groq (Llama 4)</strong></div>
                  <div><span>Storage</span><strong>Cloudinary</strong></div>
                  <div><span>Version</span><strong>v2.0 — Ultra Edition</strong></div>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* ── CONFIRM MODAL ─────────────────────────── */}
      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        danger={confirm.danger}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false })}
      />

    </div>
  );
}